class CartRemoveButton extends HTMLElement {
	constructor() {
		super();

		this.addEventListener('click', (event) => {
			event.preventDefault();
			const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
			cartItems.updateQuantity(this.dataset.index, 0);
		});
	}
}

customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
	constructor() {
		super();
		this.lineItemStatusElement =
			document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');

		const debouncedOnChange = debounce((event) => {
			this.onChange(event);
		}, ON_CHANGE_DEBOUNCE_TIMER);

		this.addEventListener('change', debouncedOnChange.bind(this));

		// Flag to prevent packaging re-check during add
		this.isAddingAdditionalProduct = false;
	}

	cartUpdateUnsubscriber = undefined;

	connectedCallback() {
		this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
			if (event.source === 'cart-items') {
				return;
			}
			this.onCartUpdate(event);
		});
	}

	disconnectedCallback() {
		if (this.cartUpdateUnsubscriber) {
			this.cartUpdateUnsubscriber();
		}
	}

	resetQuantityInput(id) {
		const input = this.querySelector(`#Quantity-${id}`);
		input.value = input.getAttribute('value');
		this.isEnterPressed = false;
	}

	setValidity(event, index, message) {
		event.target.setCustomValidity(message);
		event.target.reportValidity();
		this.resetQuantityInput(index);
		event.target.select();
	}

	validateQuantity(event) {
		const inputValue = parseInt(event.target.value);
		const index = event.target.dataset.index;
		let message = '';

		if (inputValue < event.target.dataset.min) {
			message = window.quickOrderListStrings?.min_error?.replace('[min]', event.target.dataset.min) || 
					  `Minimum quantity is ${event.target.dataset.min}`;
		} else if (event.target.max && inputValue > parseInt(event.target.max)) {
			message = window.quickOrderListStrings?.max_error?.replace('[max]', event.target.max) || 
					  `Maximum quantity is ${event.target.max}`;
		} else if (inputValue % parseInt(event.target.step) !== 0) {
			message = window.quickOrderListStrings?.step_error?.replace('[step]', event.target.step) || 
					  `Quantity must be a multiple of ${event.target.step}`;
		}

		if (message) {
			this.setValidity(event, index, message);
			// Публикуем событие чтобы сбросить loading если оно было
			publish(PUB_SUB_EVENTS.cartUpdate, { 
				source: 'cart-items-validation', 
				hasError: true 
			});
		} else {
			event.target.setCustomValidity('');
			event.target.reportValidity();
			this.updateQuantity(
				index,
				inputValue,
				document.activeElement.getAttribute('name'),
				event.target.dataset.quantityVariantId
			);
		}
	}

	onChange(event) {
		this.validateQuantity(event);
	}

	async onCartUpdate(event) {
		const skipPackagingCheck = this.isAddingAdditionalProduct || (event && event.source === 'packaging-utils');

		// Only fetch if we don't have cartData from event (optimization)
		const hasCartDataFromEvent = event && event.cartData && event.cartData.sections;
		const hasValidSections = hasCartDataFromEvent && (
			event.cartData.sections['cart-drawer'] || 
			event.cartData.sections['CartDrawer'] ||
			Object.keys(event.cartData.sections || {}).length > 0
		);

		if (this.tagName === 'CART-DRAWER-ITEMS') {
			if (hasValidSections && (event.cartData.sections['cart-drawer'] || event.cartData.sections['CartDrawer'])) {
				const sectionHtml = event.cartData.sections['cart-drawer'] || event.cartData.sections['CartDrawer'];
				const html = new DOMParser().parseFromString(sectionHtml, 'text/html');
				const sourceInner = html.querySelector('.drawer__inner');
				const targetInner = document.querySelector('#CartDrawer .drawer__inner');
				if (sourceInner && targetInner) {
					targetInner.innerHTML = sourceInner.innerHTML;
				} else {
					const selectors = ['cart-drawer-items', '.cart-drawer__footer', '.cart__footer', '.cart-block-footer'];
					for (const selector of selectors) {
						const targetElement = document.querySelector(selector);
						const sourceElement = html.querySelector(selector);
						if (targetElement && sourceElement) {
							targetElement.replaceWith(sourceElement);
						}
					}
				}
			} else {
				try {
					const response = await fetch(`${routes.cart_url}?section_id=cart-drawer`);
					const responseText = await response.text();
					const html = new DOMParser().parseFromString(responseText, 'text/html');
					const sourceInner = html.querySelector('.drawer__inner');
					const targetInner = document.querySelector('#CartDrawer .drawer__inner');
					if (sourceInner && targetInner) {
						targetInner.innerHTML = sourceInner.innerHTML;
					} else {
						const selectors = ['cart-drawer-items', '.cart-drawer__footer', '.cart__footer', '.cart-block-footer'];
						for (const selector of selectors) {
							const targetElement = document.querySelector(selector);
							const sourceElement = html.querySelector(selector);
							if (targetElement && sourceElement) {
								targetElement.replaceWith(sourceElement);
							}
						}
					}
				} catch (e) {
					// Silent error handling
				}
			}
		} else {
			if (hasValidSections && (event.cartData.sections['main-cart-items'] || event.cartData.sections['MainCartItems'])) {
				const sectionHtml = event.cartData.sections['main-cart-items'] || event.cartData.sections['MainCartItems'];
				const html = new DOMParser().parseFromString(sectionHtml, 'text/html');
				const sourceQty = html.querySelector('cart-items');
				if (sourceQty) {
					this.innerHTML = sourceQty.innerHTML;
				}

				if (event.cartData.item_count !== undefined) {
					const countElem = document.querySelector('#cart-icon-bubble .cart-count-bubble > span');
					if (countElem) countElem.textContent = event.cartData.item_count;
				}
				if (event.cartData.total_price !== undefined) {
					// Вычисляем total без упаковки
					let packagingCost = 0;
					if (window.PackagingUtils && event.cartData.items) {
						const config = window.PackagingUtils.getConfig();
						if (config && config.frozenId && config.cooledId) {
							const frozenId = String(config.frozenId);
							const cooledId = String(config.cooledId);
							
							event.cartData.items.forEach(item => {
								const variantId = String(item.variant_id || item.id);
								if (variantId === frozenId || variantId === cooledId) {
									packagingCost += item.final_line_price || 0;
								}
							});
						}
					}
					
					const totalWithoutPackaging = event.cartData.total_price - packagingCost;
					const cartTotalElem = document.querySelector('.cart__blocks .totals__total-value');
					if (cartTotalElem) cartTotalElem.textContent = totalWithoutPackaging;
				}
			} else {
				try {
					const response = await fetch(`${routes.cart_url}?section_id=main-cart-items`);
					const responseText = await response.text();
					const html = new DOMParser().parseFromString(responseText, 'text/html');
					const sourceQty = html.querySelector('cart-items');
					if (sourceQty) {
						this.innerHTML = sourceQty.innerHTML;
					}

					const totals = document.querySelector('cart-items')?.dataset.cartTotal;
					const count = document.querySelector('cart-items')?.dataset.quantity;
					const countElem = document.querySelector('#cart-icon-bubble .cart-count-bubble > span');
					const cartTotalElem = document.querySelector('.cart__blocks .totals__total-value');

					if (cartTotalElem && totals) cartTotalElem.textContent = totals;
					if (countElem && count) countElem.textContent = count;
				} catch (e) {
					// Silent error handling
				}
			}
		}

			// Реактивная проверка упаковки через PackagingUtils (debounced)
			if (!skipPackagingCheck && window.PackagingUtils) {
				const sections = this.getSectionsToRender().map((s) => s.section);
				window.PackagingUtils.ensurePackaging(
					event ? event.cartData : null,
					sections,
					(result) => {
						// Если упаковка изменилась — перерендерить UI
						if (result) {
							this.onCartUpdate({ cartData: result, source: 'packaging-utils' });
						}
					}
				);
			}
	}

	getSectionsToRender() {
		return [
			{
				id: 'main-cart-items',
				section: document.getElementById('main-cart-items').dataset.id,
				selector: '.js-contents',
			},
			{
				id: 'cart-icon-bubble',
				section: 'cart-icon-bubble',
				selector: null,
			},
			{
				id: 'cart-live-region-text',
				section: 'cart-live-region-text',
				selector: null,
			},
			{
				id: 'main-cart-footer',
				section: document.getElementById('main-cart-footer').dataset.id,
				selector: '#main-cart-footer',
			},
		];
	}

	updateQuantity(line, quantity, name, variantId) {
		// Проверяем, не выполняется ли уже запрос для этой линии
		if (!this.pendingRequests) this.pendingRequests = {};
		if (this.pendingRequests[line]) {
			console.log(`[CartItems] Update already in progress for line ${line}, skipping`);
			return;
		}
		
		this.enableLoading(line);
		this.pendingRequests[line] = true;

		const body = JSON.stringify({
			line,
			quantity,
			sections: this.getSectionsToRender().map((section) => section.section),
			sections_url: window.location.pathname,
		});

		fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
			.then((response) => {
				return response.text();
			})
			.then((state) => {
				const parsedState = JSON.parse(state);
				const quantityElement =
					document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
				const items = document.querySelectorAll('.cart-item');

				if (parsedState.errors) {
					// Ошибка от API (например, превышено количество на складе)
					// Пытаемся извлечь максимальное доступное количество из ошибки
					const errorMessage = parsedState.errors;
					let maxAvailable = null;
					
					// Shopify возвращает что-то вроде "You can only add 5 of this item to your cart"
					// Извлекаем число из сообщения
					const match = errorMessage.match(/(\d+)/);
					if (match) {
						maxAvailable = parseInt(match[1]);
					}
					
					// Или проверяем актуальное количество в корзине из parsedState
					if (!maxAvailable && parsedState.items && parsedState.items[line - 1]) {
						maxAvailable = parsedState.items[line - 1].quantity;
					}
					
					if (maxAvailable && quantityElement) {
						console.log(`[CartItems] Adjusting quantity to max available: ${maxAvailable}`);
						
						// Устанавливаем максимально доступное количество в input
						quantityElement.value = maxAvailable;
						quantityElement.setAttribute('value', maxAvailable);
						quantityElement.setAttribute('data-max-available', maxAvailable);
						
						// Запоминаем максимум для этой линии (после перерисовки DOM подставим снова)
						if (!this._lastMaxAvailableByLine) this._lastMaxAvailableByLine = {};
						this._lastMaxAvailableByLine[line] = maxAvailable;
						
						// Сразу блокируем кнопку + (значение уже = максимуму)
						const container = quantityElement.closest('.cart-block-item-count') || quantityElement.closest('quantity-input');
						const plusBtn = container?.querySelector("button[name='plus']");
						if (plusBtn) {
							plusBtn.classList.add('disabled');
							plusBtn.disabled = true;
						}
						// Обновляем состояние кнопок +/- через компонент
						const quantityInputEl = quantityElement.closest('quantity-input');
						if (quantityInputEl && typeof quantityInputEl.validateQtyRules === 'function') {
							quantityInputEl.validateQtyRules();
						}
						
						// Показываем информационное сообщение
						const infoMessage = this.getMaxAvailableMessage(maxAvailable);
						this.updateLiveRegions(line, infoMessage);
						
						// Очищаем блокировку для этой линии, чтобы можно было сделать новый запрос
						if (this.pendingRequests) {
							delete this.pendingRequests[line];
						}
						
						// Делаем повторный запрос с правильным количеством
						// Задержка 300ms чтобы пользователь увидел сообщение
						setTimeout(() => {
							this.updateLiveRegions(line, '');
							// Рекурсивный вызов с корректным значением
							this.updateQuantity(line, maxAvailable, name, variantId);
						}, 300);
					} else {
						// Если не удалось определить максимум, возвращаем старое значение
						if (quantityElement) {
							const oldValue = quantityElement.getAttribute('value');
							quantityElement.value = oldValue;
						}
						this.updateLiveRegions(line, errorMessage);
						
						// Публикуем событие об ошибке
						publish(PUB_SUB_EVENTS.cartUpdate, { 
							source: 'cart-items', 
							cartData: parsedState, 
							variantId: variantId,
							hasError: true
						});
					}
					
					return;
				}

				this.classList.toggle('is-empty', parsedState.item_count === 0);
				const cartDrawerWrapper = document.querySelector('cart-drawer');
				const cartFooter = document.getElementById('main-cart-footer');

				if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
				if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

				this.getSectionsToRender().forEach((section) => {
					const elementToReplace =
						document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
					elementToReplace.innerHTML = this.getSectionInnerHTML(
						parsedState.sections[section.section],
						section.selector
					);
				});
				
				// Восстанавливаем data-max-available и блокируем кнопку + (после замены DOM откладываем на следующий тик, чтобы custom elements успели подключиться)
				if (this._lastMaxAvailableByLine) {
					const maxByLine = { ...this._lastMaxAvailableByLine };
					setTimeout(() => {
						Object.keys(maxByLine).forEach((lineKey) => {
							const maxVal = maxByLine[lineKey];
							const input = document.getElementById(`Drawer-quantity-${lineKey}`) || document.getElementById(`Quantity-${lineKey}`);
							if (input) {
								input.setAttribute('data-max-available', String(maxVal));
								const qtyInput = input.closest('quantity-input');
								if (qtyInput && typeof qtyInput.validateQtyRules === 'function') {
									qtyInput.validateQtyRules();
								} else {
									// Прямая блокировка кнопки + если custom element ещё не готов
									const container = input.closest('.cart-block-item-count') || input.closest('quantity-input');
									const plusBtn = container?.querySelector("button[name='plus']");
									const currentVal = parseInt(input.value, 10) || 0;
									if (plusBtn && currentVal >= maxVal) {
										plusBtn.classList.add('disabled');
										plusBtn.disabled = true;
									}
								}
							}
						});
					}, 0);
				}
				
				const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
				let message = '';
				if (items.length === parsedState.items.length && updatedValue !== parseInt(quantityElement.value)) {
					if (typeof updatedValue === 'undefined') {
						message = window.cartStrings.error;
					} else {
						message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
					}
				}
				this.updateLiveRegions(line, message);

				const lineItem =
					document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
				if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
					cartDrawerWrapper
						? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`))
						: lineItem.querySelector(`[name="${name}"]`).focus();
				} else if (parsedState.item_count === 0 && cartDrawerWrapper) {
					trapFocus(cartDrawerWrapper.querySelector('#CartDrawer'), cartDrawerWrapper.querySelector('.cart-block-close') || cartDrawerWrapper.querySelector('a'));
				} else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
					trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'));
				}

				// Реактивная проверка упаковки после изменения количества
				if (window.PackagingUtils && !this.isAddingAdditionalProduct) {
					window.PackagingUtils.invalidateCache();
					const sections = this.getSectionsToRender().map((s) => s.section);
					window.PackagingUtils.ensurePackaging(parsedState, sections, (result) => {
						if (result) {
							this.onCartUpdate({ cartData: result, source: 'packaging-utils' });
						}
					});
				}

				publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId: variantId });
			})
			.catch((error) => {
				console.error('[CartItems] Update quantity failed:', error);
				this.querySelectorAll('.loading__spinner').forEach((overlay) => overlay.classList.add('hidden'));
				const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
				if (errors) errors.textContent = window.cartStrings.error;
				
				// Публикуем событие об ошибке, чтобы сбросить loading state
				publish(PUB_SUB_EVENTS.cartUpdate, { 
					source: 'cart-items', 
					hasError: true,
					variantId: variantId 
				});
			})
			.finally(() => {
				this.disableLoading(line);
				// Освобождаем блокировку запросов для этой линии
				if (this.pendingRequests) {
					delete this.pendingRequests[line];
				}
			});
	}

	updateLiveRegions(line, message) {
		const lineItemError =
			document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
		if (lineItemError) {
			const errorTextElement = lineItemError.querySelector('.cart-item__error-text');
			if (errorTextElement) {
				errorTextElement.textContent = message;
				
				// Определяем тип сообщения (ошибка или информация)
				const isInfoMessage = message && (
					message.includes('Максимально') || 
					message.includes('Maximum') || 
					message.includes('Maximal')
				);
				
				if (isInfoMessage) {
					errorTextElement.setAttribute('data-info', 'true');
				} else {
					errorTextElement.removeAttribute('data-info');
				}
			}
			
			// Добавляем визуальную индикацию к quantity-input только для ошибок
			const quantityInput = lineItemError.closest('.cart-block-item-count-delete')?.querySelector('quantity-input');
			if (quantityInput && message) {
				const isInfoMessage = message.includes('Максимально') || message.includes('Maximum') || message.includes('Maximal');
				if (!isInfoMessage) {
					quantityInput.classList.add('has-error');
					// Убираем класс ошибки через 2 секунды
					setTimeout(() => {
						quantityInput.classList.remove('has-error');
					}, 2000);
				}
			}
		}

		if (this.lineItemStatusElement) {
			this.lineItemStatusElement.setAttribute('aria-hidden', true);
		}

		const cartStatus =
			document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
		if (cartStatus) {
			cartStatus.setAttribute('aria-hidden', false);
			setTimeout(() => {
				cartStatus.setAttribute('aria-hidden', true);
			}, 1000);
		}
	}

	getMaxAvailableMessage(quantity) {
		// Возвращаем локализованное сообщение в зависимости от языка
		const locale = document.documentElement.lang || 'de';
		
		if (locale === 'ru') {
			return `Максимально доступно: ${quantity} шт.`;
		} else if (locale === 'en') {
			return `Maximum available: ${quantity} pcs`;
		} else {
			// Немецкий по умолчанию
			return `Maximal verfügbar: ${quantity} Stk.`;
		}
	}

	getSectionInnerHTML(html, selector) {
		const effectiveSelector = selector || '.shopify-section';
		const parsedHtml = new DOMParser().parseFromString(html, 'text/html');
		const element = parsedHtml.querySelector(effectiveSelector);
		if (!element) {
			return '';
		}
		return element.innerHTML;
	}

	enableLoading(line) {
		const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
		mainCartItems.classList.add('cart__items--disabled');

		const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
		const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

		[...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove('hidden'));

		document.activeElement.blur();
		this.lineItemStatusElement.setAttribute('aria-hidden', false);
	}

	disableLoading(line) {
		const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
		if (mainCartItems) {
			mainCartItems.classList.remove('cart__items--disabled');
		}

		// Скрываем загрузчики для конкретной линии
		const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
		const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

		cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
		cartDrawerItemElements.forEach((overlay) => overlay.classList.add('hidden'));

		// Дополнительно: скрываем ВСЕ видимые загрузчики как страховка
		// (на случай если элементы пересоздались и line изменился)
		const allSpinners = document.querySelectorAll('.loading__spinner:not(.hidden)');
		allSpinners.forEach((spinner) => {
			// Проверяем, что это именно cart spinner
			const isInCart = spinner.closest('cart-items') || spinner.closest('cart-drawer-items') || spinner.closest('quantity-input');
			if (isInCart) {
				spinner.classList.add('hidden');
			}
		});
	}
}

customElements.define('cart-items', CartItems);

if (!customElements.get('cart-note')) {
	customElements.define(
		'cart-note',
		class CartNote extends HTMLElement {
			constructor() {
				super();

				this.addEventListener(
					'input',
					debounce((event) => {
						const body = JSON.stringify({ note: event.target.value });
						fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
					}, ON_CHANGE_DEBOUNCE_TIMER)
				);
			}
		}
	);
}
